import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Player, Team } from 'modules/players/domain/interfaces/player';
import { kickTournamentUserFromTeam } from 'modules/players/store/actions';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TEAM_DEFAULT_LOGO } from 'shared/constants/links';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { UDEntityRow } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

interface PlayerTeamRowProps {
  team: Team;
  player: Player;
  /** Порядковый номер строки, уже готовый к показу. */
  index: number;
}

/**
 * Строка списка команд игрока: номер, эмблема, название, дата создания,
 * «Редактировать» и «Исключить» — плюс окно подтверждения исключения.
 *
 * Своё окно у каждой строки: общий на весь список показывал бы вопрос не про ту
 * команду.
 */
const PlayerTeamRow = ({ team, player, index }: PlayerTeamRowProps) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const dispatchWithToast = useDispatchWithToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const open = useCallback(() => setIsModalOpen(true), []);
  const close = useCallback(() => setIsModalOpen(false), []);

  const playerName = [player.surname, player.name, player.middleName].filter(Boolean).join(' ');
  const teamName = team.name;
  const createdAt = format(new Date(team.createdAt), 'dd.MM.yyyy');

  const handleClickTeam = useCallback(() => {
    navigate(Pages.TEAMS.builders.edit(team.id), { state: { fromPlayer: player.id } });
  }, [navigate, player.id, team.id]);

  const handleKickFromTeam = useCallback(async () => {
    // При ошибке хелпер возвращает undefined и уже показал тост — окно оставляем открытым.
    const result = await dispatchWithToast(kickTournamentUserFromTeam({ userId: player.id, teamId: team.id }), {
      success: t`Игрок исключен из команды`,
      error: msg`Не удалось исключить игрока`,
    });
    if (result === undefined) return;

    close();
  }, [close, dispatchWithToast, player.id, t, team.id]);

  return (
    <>
      <UDEntityRow
        index={index}
        // Эмблема приходит пустой строкой, а не `null`, поэтому `||`, а не `??`.
        avatar={team.emblem?.externalUrl || TEAM_DEFAULT_LOGO}
        title={team.name}
        subtitle={t`Дата создания: ${createdAt}`}
        actions={
          <>
            <UDButton variant="primary" onClick={handleClickTeam}>
              <Trans>Редактировать</Trans>
            </UDButton>

            <UDButton variant="secondary" onClick={open}>
              <Trans>Исключить</Trans>
            </UDButton>
          </>
        }
      />

      <Modal.Confirm
        show={isModalOpen}
        title={t`Исключить игрока «${playerName}» из команды «${teamName}»?`}
        description={t`Это действие необратимо.`}
        confirmText={t`Исключить`}
        onConfirm={handleKickFromTeam}
        onCancel={close}
      />
    </>
  );
};

export default React.memo(PlayerTeamRow);
